import React, { useState, useEffect } from 'react';
import { 
  MoreVertical, 
  Columns, 
  Filter, 
  Eye, 
  Trash2, 
  RefreshCcw, 
  ChevronDown, 
  ChevronUp,
  Save,
  Check,
  UserPlus,
  UserMinus,
  CheckSquare,
  Send,
  FileEdit,
  Hash,
  Globe,
  DollarSign,
  Image,
  RotateCw,
  Briefcase
} from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

/**
 * EavAction: Bulk actions dropdown
 */
export const EavAction = ({ selectedIds, onBatchDelete, onBatchUpdate, onBatchAssignResponsible, onBatchRemoveResponsible, onBatchQcUpdate, onBatchPushToChannel, onBatchRecalculateFormulas, onBatchEditStatus, onBatchGenerateUrlKey, onBatchUpdateSyncStatus, onBatchUpdatePushStatus, onBatchUpdateOnline, onBatchUpdatePrice, onBatchUpdateImages, onBatchAssignPosition }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  if (selectedIds.length === 0) return null;

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full md:w-auto flex items-center justify-center md:justify-start gap-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors dark:text-white"
      >
        <MoreVertical size={16} />
        {t('common.batch_actions_with_count', { count: selectedIds.length })}
        <ChevronDown size={14} />
      </button>
      
      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-full md:w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-[9999] py-2 animate-in fade-in slide-in-from-top-2 overflow-visible">
          {/* Batch update - only shown when user has edit permission */}
          {onBatchUpdate && (
            <>
              <button 
                onClick={() => { onBatchUpdate(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <RefreshCcw size={16} className="text-blue-500" />
                {t('common.batch_update_attributes')}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {/* Batch QC check */}
          {onBatchQcUpdate && (
            <>
              <button 
                onClick={() => { onBatchQcUpdate(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <CheckSquare size={16} className="text-emerald-500" />
                {t('qa_batch_update') || 'Batch QC'}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {/* Batch assign responsible person */}
          {onBatchPushToChannel && (
            <>
              <button 
                onClick={() => { onBatchPushToChannel(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <Send size={16} className="text-violet-500" />
                {t('channel.push_to_channel')}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {onBatchRecalculateFormulas && (
            <>
              <button 
                onClick={() => { onBatchRecalculateFormulas(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <RefreshCcw size={16} className="text-orange-500" />
                {t('channel.recalculate_formulas') || 'Recalculate Formulas'}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {/* Batch edit status */}
          {onBatchEditStatus && (
            <>
              <button 
                onClick={() => { onBatchEditStatus(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <FileEdit size={16} className="text-amber-500" />
                {t('channel.batch_edit_status') || 'Batch Edit Status'}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {/* Batch generate URL key */}
          {onBatchGenerateUrlKey && (
            <>
              <button 
                onClick={() => { onBatchGenerateUrlKey(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <Hash size={16} className="text-sky-500" />
                {t('channel.batch_generate_url_key') || 'Batch Generate URL Key'}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {onBatchUpdateSyncStatus && (
            <>
              <button 
                onClick={() => { onBatchUpdateSyncStatus(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <RotateCw size={16} className="text-indigo-500" />
                {t('channel.batch_update_sync_status') || '重设同步状态'}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {onBatchUpdatePushStatus && (
            <>
              <button 
                onClick={() => { onBatchUpdatePushStatus(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <RotateCw size={16} className="text-purple-500" />
                {t('products.batch_update_push_status') || '重设推送状态'}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {onBatchRemoveResponsible && (
            <>
              <button 
                onClick={() => { onBatchRemoveResponsible(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <UserMinus size={16} className="text-red-500" />
                {t('common.batch_remove_responsible')}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {onBatchAssignResponsible && (
            <>
              <button 
                onClick={() => { onBatchAssignResponsible(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <UserPlus size={16} className="text-violet-500" />
                {t('common.batch_assign_responsible')}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {/* Batch update online attributes */}
          {onBatchUpdateOnline && (
            <>
              <button 
                onClick={() => { onBatchUpdateOnline(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <Globe size={16} className="text-cyan-500" />
                {t('channel.update_online') || 'Update Online'}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {/* Batch update price */}
          {onBatchUpdatePrice && (
            <>
              <button 
                onClick={() => { onBatchUpdatePrice(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <DollarSign size={16} className="text-yellow-500" />
                {t('channel.update_price') || 'Update Price'}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {/* Batch update images */}
          {onBatchUpdateImages && (
            <>
              <button 
                onClick={() => { onBatchUpdateImages(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <Image size={16} className="text-purple-500" />
                {t('channel.update_images') || 'Update Images'}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {/* Batch assign position */}
          {onBatchAssignPosition && (
            <>
              <button 
                onClick={() => { onBatchAssignPosition(selectedIds); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <Briefcase size={16} className="text-indigo-500" />
                {t('employees.batch_assign_position')}
              </button>
              <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
            </>
          )}
          {/* Batch delete */}
          {onBatchDelete && (
            <button 
              onClick={() => { onBatchDelete(selectedIds); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            >
              <Trash2 size={16} />
              {t('common.batch_delete')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * EavColumns: Column visibility toggle
 */
export const EavColumns = ({ allColumns, visibleColumns, setVisibleColumns }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full md:w-auto flex items-center justify-center md:justify-start gap-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors dark:text-white"
      >
        <Columns size={16} />
        {t('common.column_config')}
        <ChevronDown size={14} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-full md:w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-[9999] p-4 animate-in fade-in slide-in-from-top-2 overflow-visible">
          <h3 className="text-xs font-semibold text-slate-400 dark:text-slate-500 mb-3">{t('common.display_fields')}</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {allColumns.map(col => (
              <label key={col.code} className="flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-md cursor-pointer transition-colors">
                <input 
                  type="checkbox" 
                  checked={visibleColumns.includes(col.code)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setVisibleColumns([...visibleColumns, col.code]);
                    } else {
                      setVisibleColumns(visibleColumns.filter(c => c !== col.code));
                    }
                  }}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">{col.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * EavFilters: Expandable filter panel
 */
export const EavFilters = ({ isExpanded, setIsExpanded, columns, filters, setFilters }) => {
  const { t } = useTranslation();
  
  const renderInput = (col) => {
    const value = filters[col.code] || '';
    const onChange = (val) => setFilters({ ...filters, [col.code]: val });

    switch (col.inputType) {
      case 'Dropdown':
      case 'Multiple Select':
        return (
          <select 
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          >
            <option value="">{t('common.all')}</option>
            {col.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      case 'Date':
      case 'Date and Time':
        return (
          <input 
            type="date" 
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            placeholder={t('common.date_placeholder')}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          />
        );
      case 'Yes/No':
        return (
          <select 
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          >
            <option value="">{t('common.all')}</option>
            <option value="1">{t('common.yes')}</option>
            <option value="0">{t('common.no')}</option>
          </select>
        );
      default:
        return (
          <input 
            type="text" 
            value={value} 
            onChange={(e) => onChange(e.target.value)}
            placeholder={t('eav.filter_by', {field: col.label})}
            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
          />
        );
    }
  };

  return (
    <div className="w-full">
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors mb-4 dark:text-white"
      >
        <Filter size={16} />
        {t('common.filters')}
        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {isExpanded && (
        <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 mb-6 animate-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {columns.map(col => (
              <div key={col.code} className="space-y-2">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">{col.label}</label>
                {renderInput(col)}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button 
              onClick={() => setFilters({})}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              {t('common.reset_all')}
            </button>
            <button 
              className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 shadow-md transition-all"
            >
              {t('common.apply_filters')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * EavView: Saved view management
 */
export const EavView = ({ currentView, views, onSelectView, onSaveView, onDeleteView }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const { t } = useTranslation();

  return (
    <div className="relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full md:w-auto flex items-center justify-center md:justify-start gap-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-3 py-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium transition-colors dark:text-white"
      >
        <Eye size={16} />
        {t('common.current_view')}: <span className="text-blue-600 dark:text-blue-400">{currentView}</span>
        <ChevronDown size={14} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-full md:w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-[9999] p-4 animate-in fade-in slide-in-from-top-2 overflow-visible">
          <h3 className="text-xs font-semibold text-slate-400 dark:text-slate-500 mb-3">{t('common.saved_views')}</h3>
          <div className="space-y-1 mb-4">
            {views.map((view, idx) => (
              <div key={view.name || idx}
                className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${currentView === view.name ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
              >
                <button
                  onClick={() => { onSelectView(view); setIsOpen(false); }}
                  className="flex-1 text-left"
                >
                  {view.name}
                </button>
                {onDeleteView && view.columns && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteView(view); }}
                    className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    title="Delete view"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 dark:border-slate-700 pt-4 space-y-2">
            <input 
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              placeholder={t('common.new_view_name')}
              className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
            />
            <button 
              onClick={() => { onSaveView(newViewName); setNewViewName(''); setIsOpen(false); }}
              disabled={!newViewName}
              className="w-full flex items-center justify-center gap-2 bg-slate-900 dark:bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
            >
              <Save size={16} />
              {t('common.save_current_layout')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
